/*
 * FloatingChatPanel
 *
 * Panel flotante de chat embebido sobre la vista WebRTC de un agente.
 * Reutiliza el mismo chatStore + API que ChatConversationPage para que el
 * admin pueda conversar con el agente que está viendo sin perder el foco
 * del control remoto (teclado/ratón).
 *
 * Estados:
 *   - !open           → no renderiza nada
 *   - open + minimizado → píldora/chip compacto (hostname + unread badge + restaurar)
 *   - open + expanded  → Paper flotante con header, lista de mensajes e input
 *
 * Decisión de teclado:
 *   El WebRTCPage registra `keydown`/`keyup` en `window` con capture:true.
 *   Cuando el TextField del chat tiene foco, queremos que las teclas
 *   vayan al chat (Enter → enviar, Shift+Enter → newline) y NO al host
 *   remoto.  Los eventos que React emite desde el TextField NO llegan a
 *   la fase de capture del listener de window en orden distinto, pero
 *   igualmente el listener hace preventDefault + stopPropagation y
 *   manda la tecla al host.
 *
 *   WebRTCPage recognizes `data-floating-chat-panel` in its native capture
 *   listener and focus handler, so typing and Enter stay in the chat rather
 *   than being injected into the remote desktop.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  Badge,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Remove as RemoveIcon,
  Computer as ComputerIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useChatStore } from '@/store/chatStore';
import { fetchMessages, markConversationRead } from '@/api/chat';
import { useWebSocket } from '@/hooks/useWebSocket';
import ChatMessageBubble from '@/components/chat/ChatMessageBubble';

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Hoy';
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
  return d.toLocaleDateString();
}

export interface FloatingChatPanelProps {
  agentId: string;
  hostname: string;
  open: boolean;
  minimized: boolean;
  onClose: () => void;
  onToggleMinimize: () => void;
  /** Anchor opcional para que el panel flote relativo al contenedor del video. */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

const PANEL_WIDTH = 360;
const PANEL_HEIGHT = 520;

export const FloatingChatPanel: React.FC<FloatingChatPanelProps> = ({
  agentId,
  hostname,
  open,
  minimized,
  onClose,
  onToggleMinimize,
  anchorRef,
}) => {
  const { send } = useWebSocket();
  const { conversations, messagesByConv, setMessages, ensureConversation } = useChatStore();

  const conv = conversations.find((c) => c.agentId === agentId);
  const convId = conv?.conversationId;
  const unread = conv?.unreadCount ?? 0;

  const messages = useMemo(
    () => (convId ? (messagesByConv[convId] ?? []) : []),
    [convId, messagesByConv]
  );

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);

  const showLoading = !!agentId && (resolving || (!!convId && loading));

  // El store de conversaciones sólo se hidrata en /chat. Sin este lookup el
  // panel abierto sobre la vista del agente se quedaba sin conversationId y
  // por lo tanto sin historial (se veía en blanco).
  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    ensureConversation(agentId)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, ensureConversation]);

  // Load messages when we have a conversationId
  useEffect(() => {
    if (!convId) {
      // showLoading uses !!convId so the spinner stays hidden.
      return;
    }
    let cancelled = false;
    fetchMessages(convId)
      .then((msgs) => {
        if (cancelled) return;
        setMessages(convId, msgs);
        setError(null);
        // Mark as read when the user opens / un-minimizes the panel
        markConversationRead(convId).catch(() => {});
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [convId, setMessages, open, minimized]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Mark as read when new agent messages arrive while open & not minimized
  useEffect(() => {
    if (!convId || messages.length === 0 || minimized || !open) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg?.senderType === 'agent' && !lastMsg.readAt) {
      markConversationRead(convId).catch(() => {});
    }
  }, [convId, messages, minimized, open]);

  const handleSend = useCallback(() => {
    const content = input.trim();
    if (!content || !agentId) return;
    setSending(true);
    const ok = send({ type: 'chat.send', data: { agentId, content } });
    if (ok) {
      setInput('');
    }
    setSending(false);
  }, [input, agentId, send]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDragStart = (event: React.PointerEvent<HTMLElement>) => {
    if ((event.target as Element).closest('button')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { offsetX: event.clientX - rect.left, offsetY: event.clientY - rect.top };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const handleDragMove = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || !panel) return;
    const bounds = (anchorRef?.current ?? document.documentElement).getBoundingClientRect();
    setPosition({
      x: Math.max(
        0,
        Math.min(event.clientX - bounds.left - drag.offsetX, bounds.width - panel.offsetWidth)
      ),
      y: Math.max(
        0,
        Math.min(event.clientY - bounds.top - drag.offsetY, bounds.height - panel.offsetHeight)
      ),
    });
  };

  const panelPosition = position
    ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' }
    : { right: 16, bottom: 16 };

  // Group messages by date
  const grouped: Array<{ date: string; messages: typeof messages }> = [];
  for (const msg of messages) {
    const dateKey = formatDate(msg.createdAt);
    const last = grouped[grouped.length - 1];
    if (last && last.date === dateKey) {
      last.messages.push(msg);
    } else {
      grouped.push({ date: dateKey, messages: [msg] });
    }
  }

  if (!open) return null;

  // ── Minimized state: render a compact pill ────────────────────────────────
  if (minimized) {
    return (
      <Box
        ref={panelRef}
        data-floating-chat-panel="true"
        onClick={onToggleMinimize}
        role="button"
        aria-label={`Restaurar chat con ${hostname}`}
        tabIndex={0}
        sx={{
          position: anchorRef ? 'absolute' : 'fixed',
          ...panelPosition,
          zIndex: 1300,
          cursor: 'pointer',
          userSelect: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          pl: 1.25,
          pr: 0.75,
          py: 0.5,
          borderRadius: 999,
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(4px)',
          boxShadow: 4,
          border: '1px solid',
          borderColor: 'divider',
          transition: (theme) => theme.transitions.create(['box-shadow', 'transform']),
          '&:hover': {
            boxShadow: 8,
            transform: 'translateY(-1px)',
          },
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Badge
          color="error"
          badgeContent={unread}
          max={99}
          overlap="circular"
          sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
        >
          <ChatBubbleOutlineIcon fontSize="small" color="primary" />
        </Badge>
        <Typography variant="body2" sx={{ fontWeight: 500, maxWidth: 160 }} noWrap>
          {hostname || agentId}
        </Typography>
        <IconButton
          size="small"
          aria-label="Cerrar chat"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          sx={{ ml: 0.25 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  // ── Expanded state: full floating panel ──────────────────────────────────
  return (
    <Box
      ref={panelRef}
      data-floating-chat-panel="true"
      sx={{
        position: anchorRef ? 'absolute' : 'fixed',
        ...panelPosition,
        width: PANEL_WIDTH,
        height: PANEL_HEIGHT,
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          pointerEvents: 'auto',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.98),
          backdropFilter: 'blur(4px)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={() => {
            dragRef.current = null;
          }}
          onPointerCancel={() => {
            dragRef.current = null;
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            cursor: 'grab',
            touchAction: 'none',
          }}
        >
          <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}>
            <ComputerIcon fontSize="small" />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={600} variant="body2" noWrap>
              {hostname || agentId}
            </Typography>
            <Chip
              size="small"
              label={conv ? (conv.status === 'archived' ? 'Archivado' : 'Chat') : 'Nuevo'}
              color={conv ? 'default' : 'primary'}
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          </Box>
          <Tooltip title="Minimizar chat">
            <IconButton size="small" onClick={onToggleMinimize} aria-label="Minimizar chat">
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cerrar chat">
            <IconButton size="small" onClick={onClose} aria-label="Cerrar chat">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Messages area */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 1.5, py: 1 }}>
          {showLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!showLoading && !error && messages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary" variant="body2">
                Sin mensajes aún. Iniciá la conversación.
              </Typography>
            </Box>
          )}

          {grouped.map((group) => (
            <Box key={group.date}>
              <Divider sx={{ my: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {group.date}
                </Typography>
              </Divider>
              {group.messages.map((msg) => (
                <ChatMessageBubble key={msg.messageId} msg={msg} compact />
              ))}
            </Box>
          ))}
          <div ref={bottomRef} />
        </Box>

        {/* Input area */}
        <Paper
          elevation={0}
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            display: 'flex',
            gap: 0.5,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            placeholder="Escribí un mensaje…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || sending}
            sx={{ mb: 0.25 }}
            aria-label="Enviar mensaje"
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Paper>
    </Box>
  );
};
